import React from 'react';
import { Target, Heart, Shield, Users, Lightbulb, Mail, FileText, Navigation, BookOpen } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Community-Centered',
      description: 'Centering the voices, experiences, and leadership of Black youth, families, and communities most impacted by systemic gaps.'
    },
    {
      icon: Shield,
      title: 'Trauma-Informed',
      description: 'Approaching all engagement with sensitivity to trauma, prioritizing safety, dignity, and trust in every interaction.'
    },
    {
      icon: Target,
      title: 'Accountability',
      description: 'Advocating for transparent, timely, and equitable responses from institutions while holding systems accountable to communities.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Building partnerships across sectors—justice, child welfare, education, health, and community agencies—for coordinated support.'
    },
    {
      icon: Lightbulb,
      title: 'Evidence-Based Advocacy',
      description: 'Using lived-experience data, community research, and systems analysis to drive meaningful policy and practice change.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-12 bg-navy">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            About Our Initiative
          </h1>
          <p className="text-xl text-gray-300 text-center max-w-3xl mx-auto leading-relaxed">
            A province-wide advocacy and systems-navigation initiative actively supporting Black youth and families 
            impacted by disappearance, exploitation, trafficking risk, and system involvement across Ontario.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-navy p-8 md:p-12 rounded-lg border border-gold-dim">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
                <Target size={32} className="text-gold" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                <strong className="text-white">Find Ontario Missing Boys (FOMB)</strong> is a province-wide, 
                community-led advocacy initiative founded by producer and systems advocate <strong className="text-white">Shana McCalla, Founder & Lead Advocate</strong>, whose 
                investigative and advocacy work has helped elevate awareness and dialogue around Black youth 
                disappearances, exploitation, and systemic response gaps across Ontario.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Because many families encounter fragmented or delayed institutional responses during crisis, FOMB serves as an early point of trust and coordination — helping families navigate complex systems while strengthening communication between community and institutional partners. This bridging role allows lived-experience insight to inform prevention strategies, coordinated responses, and long-term systemic improvements across Ontario.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Grounded in direct frontline engagement with families, caregivers, educators, and community agencies, 
                FOMB operates at the intersection of prevention, advocacy, and systems navigation—actively supporting families 
                during crisis while working to improve institutional responses across justice, child welfare, education, 
                and community safety sectors. Through lived-experience leadership, trusted grassroots relationships, 
                and active collaboration with partners across the province, FOMB is building coordinated response 
                mechanisms toward safer outcomes, early intervention, and long-term systemic change for vulnerable youth.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                As FOMB continues to expand its reach, the initiative is positioned to scale coordinated prevention 
                and response pathways across Ontario through strategic partnerships and collaborative funding 
                opportunities that strengthen community-based support systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-navy">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Our Approach & Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="value-card bg-charcoal p-8 rounded-lg border border-gold-dim hover:border-gold transition-all">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 rounded-full mb-6">
                    <Icon size={28} className="text-gold" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Do - Detailed */}
      <section className="py-16 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">How We Support Communities</h2>
            
            <div className="space-y-6">
              <div className="activity-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <Heart size={24} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Direct Family & Caregiver Support</h3>
                    <p className="text-gray-400 leading-relaxed">
                      We provide crisis navigation, emotional support, and practical guidance for families experiencing 
                      youth disappearance, exploitation, or system involvement. Families receive trusted, trauma-informed 
                      assistance during their most difficult moments.
                    </p>
                  </div>
                </div>
              </div>

              <div className="activity-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <Navigation size={24} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Systems Navigation & Institutional Advocacy</h3>
                    <p className="text-gray-400 leading-relaxed">
                      We help families understand and navigate complex systems—police, child welfare, education, 
                      health, and community services—while advocating for timely, coordinated, and equitable responses 
                      from institutions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="activity-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <BookOpen size={24} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Prevention & Community Education</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Through trauma-informed workshops, community dialogues, and educational resources, we build 
                      awareness about youth exploitation prevention, early warning signs, prevention pathways, 
                      and available supports.
                    </p>
                  </div>
                </div>
              </div>

              <div className="activity-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <FileText size={24} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Research, Documentation & Policy Advocacy</h3>
                    <p className="text-gray-400 leading-relaxed">
                      We collect lived-experience data, document systemic response patterns, and use community-based 
                      evidence to advocate for policy changes, improved training, and better coordination across 
                      child-serving sectors province-wide.
                    </p>
                  </div>
                </div>
              </div>

              <div className="activity-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <Users size={24} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Cross-Sector Partnership Building</h3>
                    <p className="text-gray-400 leading-relaxed">
                      FOMB actively collaborates with community organizations, justice partners, child welfare agencies, 
                      educators, health providers, and municipal leaders to create coordinated pathways that improve 
                      outcomes for vulnerable youth across Ontario.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-navy">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <Mail size={32} className="text-gold" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">Get In Touch</h2>
            <p className="text-gray-300 mb-8 leading-relaxed">
              Have questions, want to get involved, or need more information about our work?
            </p>
            
            <div className="bg-charcoal p-8 rounded-lg border border-gold-dim inline-block">
              <p className="text-sm text-gray-400 mb-2">Email us at:</p>
              <a 
                href="mailto:findontariomissingboys@gmail.com"
                className="text-2xl font-bold text-gold hover:text-white transition-colors break-all"
              >
                findontariomissingboys@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
